import {
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useRouteError,
} from "react-router";

import type { Route } from "./+types/root";
import styles from "./tailwind.css?inline";
import AppLayout from "./components/Layout";
import { getOptionalUser } from "./lib/auth.server";
import type { AppEnv } from "./lib/env.server";
import { parseThemeFromCookieString } from "./hooks/useTheme";

import interLatin400 from '@fontsource/inter/files/inter-latin-400-normal.woff2?url';
import interLatin600 from '@fontsource/inter/files/inter-latin-600-normal.woff2?url';
import jbMonoLatin400 from '@fontsource/jetbrains-mono/files/jetbrains-mono-latin-400-normal.woff2?url';
import jbMonoLatin500 from '@fontsource/jetbrains-mono/files/jetbrains-mono-latin-500-normal.woff2?url';

/**
 * Anti-FOUC theme script. Runs synchronously in <head> before any styles are
 * applied so <html> already has the right `dark` class by the time CSS is
 * parsed. The cookie value is one of `light` | `dark` | `system`. `system`
 * (and a missing cookie) defer to `prefers-color-scheme`.
 *
 * On any failure the page falls back to dark — the same default as the
 * pre-toggle build, so a broken cookie never strands a returning user in an
 * unstyled state.
 *
 * Keep this string tiny and side-effect-free apart from toggling the class;
 * it's inlined into every HTML response.
 */
const themeScript = `(() => {
  try {
    var m = document.cookie.match(/(?:^|;\\s*)theme=(light|dark|system)/);
    var pref = m ? m[1] : 'system';
    var resolved =
      pref === 'light' || pref === 'dark'
        ? pref
        : window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light';
    if (resolved === 'dark') {
      document.documentElement.classList.add('dark');
    }
  } catch (e) {
    document.documentElement.classList.add('dark');
  }
})();`;

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://assets.foyzul.com" },
  { rel: "preload", href: interLatin400, as: "font", type: "font/woff2", crossOrigin: "anonymous" },
  { rel: "preload", href: interLatin600, as: "font", type: "font/woff2", crossOrigin: "anonymous" },
  { rel: "preload", href: jbMonoLatin400, as: "font", type: "font/woff2", crossOrigin: "anonymous" },
  { rel: "preload", href: jbMonoLatin500, as: "font", type: "font/woff2", crossOrigin: "anonymous" },
];

export async function loader({ request, context }: Route.LoaderArgs) {
  const env = context.cloudflare.env as AppEnv;
  const ctx = context.cloudflare.ctx;

  try {
    const user = await getOptionalUser(request, env, ctx);
    return {
      isLoggedIn: !!user,
      userEmail: user?.email ?? "",
      initialTheme: readThemeFromRequest(request),
    };
  } catch (err) {
    // E8: Root loader resilience — KV/D1 blip should not crash public pages
    console.error("[root] Auth check failed:", err);
    return {
      isLoggedIn: false,
      userEmail: "",
      initialTheme: readThemeFromRequest(request),
    };
  }
}

function readThemeFromRequest(request: Request): "light" | "dark" | "system" {
  return parseThemeFromCookieString(request.headers.get("Cookie") ?? "");
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* Theme bootstrap MUST come before the stylesheet so <html> is
            classed correctly before CSS is parsed. See themeScript above. */}
        <script dangerouslySetInnerHTML={{ __html: themeScript }} />
        <style dangerouslySetInnerHTML={{ __html: styles }} />
        <Meta />
        <Links />
      </head>
      <body className="bg-bg-primary text-text-primary antialiased">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <AppLayout />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  console.error(error);
  let message = "Something went wrong";
  let details = "An unexpected error occurred. Please try again.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Something went wrong";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center pt-16 px-6">
      <div className="text-center max-w-md">
        <h1 className="font-heading text-h1 text-text-primary mb-4">{message}</h1>
        <p className="font-body text-body text-text-secondary mb-6">{details}</p>
        <div className="flex items-center justify-center gap-3">
          <Link
            to="/"
            className="font-heading text-sm font-medium bg-accent-cyan text-bg-primary px-5 py-2.5 rounded-md hover:bg-accent-hover hover:scale-[1.03] hover:shadow-glow transition-all duration-200 no-underline inline-block"
          >
            Go Home
          </Link>
          <button
            onClick={() => window.location.reload()}
            className="font-heading text-sm font-medium border border-border-subtle text-text-primary px-5 py-2.5 rounded-md hover:bg-bg-elevated hover:border-border-active transition-all duration-200"
          >
            Try Again
          </button>
        </div>
        {stack && (
          <pre className="w-full p-4 overflow-x-auto text-left text-xs text-text-muted mt-8 bg-bg-surface rounded-lg border border-border-subtle">
            <code>{stack}</code>
          </pre>
        )}
      </div>
    </div>
  );
}
